// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  AlertTriangle, 
  Clock, 
  CheckCircle, 
  Calendar, 
  DollarSign,
  RefreshCw,
  ChevronRight,
  Zap,
  TrendingDown,
  TrendingUp,
  Building2,
  User,
  CreditCard
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, isToday, isPast, parseISO, addDays, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface FinancialEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  payment_date?: string;
  status: string;
  created_at: string;
  client_id?: string;
  case_id?: string;
  clients?: {
    name: string;
    email: string;
    phone?: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
  payment_confirmed?: boolean;
  actual_payment_date?: string;
  payment_notes?: string;
}

interface SmartPaymentAlertsProps {
  entries: FinancialEntry[];
  onEntryUpdate: () => void;
}

type AlertCategory = 'overdue' | 'today' | 'upcoming' | 'future';

interface CategorizedEntry extends FinancialEntry {
  category_type: AlertCategory;
  days_info: number;
  is_recurring: boolean;
  recurring_entry_id?: string;
}

const isReceita = (e: FinancialEntry) => e.entry_type === 'receita';
const isDespesa = (e: FinancialEntry) => e.entry_type === 'despesa';

const SmartPaymentAlerts: React.FC<SmartPaymentAlertsProps> = ({ entries, onEntryUpdate }) => {
  const [categorizedEntries, setCategorizedEntries] = useState<{
    overdue: CategorizedEntry[];
    today: CategorizedEntry[];
    upcoming: CategorizedEntry[];
    future: CategorizedEntry[];
  }>({ overdue: [], today: [], upcoming: [], future: [] });
  
  const [selectedEntry, setSelectedEntry] = useState<CategorizedEntry | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [paymentNotes, setPaymentNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<AlertCategory>('overdue');
  // Cliente do processo, para lançamentos ligados só ao processo (sem cliente direto)
  const [caseClientNames, setCaseClientNames] = useState<Record<string, string>>({});
  const { toast } = useToast();

  useEffect(() => {
    const caseIds = [...new Set(entries.filter((e) => !e.clients?.name && e.case_id).map((e) => e.case_id as string))];
    if (caseIds.length === 0) return;
    (async () => {
      const { data: casesData } = await supabase.from('cases').select('id, client_id').in('id', caseIds);
      const clientIds = [...new Set((casesData || []).map((c: any) => c.client_id).filter(Boolean))];
      if (clientIds.length === 0) return;
      const { data: clientsData } = await supabase.from('clients').select('id, name').in('id', clientIds);
      const nameById = new Map((clientsData || []).map((c: any) => [c.id, c.name]));
      const map: Record<string, string> = {};
      (casesData || []).forEach((c: any) => {
        const name = nameById.get(c.client_id);
        if (name) map[c.id] = name;
      });
      setCaseClientNames(map);
    })();
  }, [entries]);

  // Processos administrativos guardam o nome do cliente no próprio processo
  const [adminInfo, setAdminInfo] = useState<Record<string, { client?: string; title?: string }>>({});
  useEffect(() => {
    const ids = [...new Set(entries.map((e: any) => e.administrative_process_id).filter(Boolean))] as string[];
    if (ids.length === 0) return;
    (async () => {
      const { data } = await (supabase as any)
        .from('administrative_processes')
        .select('id, title, description, client_name')
        .in('id', ids);
      const map: Record<string, { client?: string; title?: string }> = {};
      (data || []).forEach((p: any) => {
        map[p.id] = { client: p.client_name || undefined, title: p.title || p.description || undefined };
      });
      setAdminInfo(map);
    })();
  }, [entries]);

  useEffect(() => {
    categorizeEntries();
  }, [entries]);

  const categorizeEntries = async () => {
    const today = new Date();
    const threeDaysFromNow = addDays(today, 3);
    const sevenDaysFromNow = addDays(today, 7);
    
    // Buscar pagamentos recorrentes já confirmados este mês
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    
    const { data: paidRecurring } = await supabase
      .from('recurring_payments')
      .select('recurring_entry_id, payment_month, payment_year')
      .eq('payment_month', currentMonth)
      .eq('payment_year', currentYear);
    
    const paidRecurringIds = new Set(paidRecurring?.map(p => p.recurring_entry_id) || []);

    const overdue: CategorizedEntry[] = [];
    const todayList: CategorizedEntry[] = [];
    const upcoming: CategorizedEntry[] = [];
    const future: CategorizedEntry[] = [];

    entries.forEach(entry => {
      if (entry.status !== 'Pendente' || !entry.payment_date) return;

      const isVirtual = entry.id.startsWith('virtual-');
      let recurringEntryId = '';
      
      if (isVirtual) {
        const parts = entry.id.split('-');
        recurringEntryId = parts.slice(1, -2).join('-');
        if (paidRecurringIds.has(recurringEntryId)) return;
      }

      const dueDate = parseISO(entry.payment_date);
      const daysUntilDue = differenceInDays(dueDate, today);

      const categorizedEntry: CategorizedEntry = {
        ...entry,
        category_type: 'future',
        days_info: Math.abs(daysUntilDue),
        is_recurring: isVirtual || entry.description.includes('Recorrente'),
        recurring_entry_id: recurringEntryId
      };

      if (isPast(dueDate) && !isToday(dueDate)) {
        categorizedEntry.category_type = 'overdue';
        categorizedEntry.days_info = Math.abs(daysUntilDue);
        overdue.push(categorizedEntry);
      } else if (isToday(dueDate)) {
        categorizedEntry.category_type = 'today';
        categorizedEntry.days_info = 0;
        todayList.push(categorizedEntry);
      } else if (dueDate <= threeDaysFromNow) {
        categorizedEntry.category_type = 'upcoming';
        upcoming.push(categorizedEntry);
      } else if (dueDate <= sevenDaysFromNow) {
        categorizedEntry.category_type = 'future';
        future.push(categorizedEntry);
      }
    });

    // Ordenar por data
    const sortByDate = (a: CategorizedEntry, b: CategorizedEntry) => 
      new Date(a.payment_date!).getTime() - new Date(b.payment_date!).getTime();

    setCategorizedEntries({
      overdue: overdue.sort(sortByDate),
      today: todayList.sort(sortByDate),
      upcoming: upcoming.sort(sortByDate),
      future: future.sort(sortByDate)
    });

    // Set active tab to first non-empty category
    if (overdue.length > 0) setActiveTab('overdue');
    else if (todayList.length > 0) setActiveTab('today');
    else if (upcoming.length > 0) setActiveTab('upcoming');
    else if (future.length > 0) setActiveTab('future');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const openConfirmDialog = (entry: CategorizedEntry) => {
    setSelectedEntry(entry);
    setPaymentDate(format(new Date(), 'yyyy-MM-dd'));
    setPaymentNotes('');
    setIsConfirmDialogOpen(true);
  };

  const handleQuickConfirm = async (entry: CategorizedEntry) => {
    setSelectedEntry(entry);
    await confirmPayment(entry, format(new Date(), 'yyyy-MM-dd'), '');
  };

  const confirmPayment = async (entry: CategorizedEntry, date: string, notes: string) => {
    setIsProcessing(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      if (entry.is_recurring && entry.recurring_entry_id) {
        // Para entradas recorrentes, criar entrada real e registrar pagamento
        const paymentDateObj = new Date(date);
        const paymentMonth = paymentDateObj.getMonth() + 1;
        const paymentYear = paymentDateObj.getFullYear();

        // Criar entrada financeira real
        const { data: newEntry, error: entryError } = await supabase
          .from('financial_entries')
          .insert({
            description: entry.description.replace(/\s*\(Recorrente[^)]*\)/, ''),
            amount: entry.amount,
            entry_type: entry.entry_type,
            category: entry.category,
            payment_date: entry.payment_date,
            status: 'Pago',
            payment_confirmed: true,
            actual_payment_date: date,
            payment_notes: notes || '[Confirmado via Alerta Rápido]',
            client_id: entry.client_id,
            case_id: entry.case_id,
            user_id: user.id
          })
          .select()
          .single();

        if (entryError) throw entryError;

        // Registrar pagamento recorrente
        const { error: paymentError } = await supabase
          .from('recurring_payments')
          .insert({
            recurring_entry_id: entry.recurring_entry_id,
            payment_month: paymentMonth,
            payment_year: paymentYear,
            payment_date: date,
            actual_payment_date: date,
            amount: entry.amount,
            payment_notes: notes,
            financial_entry_id: newEntry.id,
            user_id: user.id
          });

        if (paymentError) throw paymentError;
      } else {
        // Para entradas normais
        const { error } = await supabase
          .from('financial_entries')
          .update({
            status: 'Pago',
            payment_confirmed: true,
            actual_payment_date: date,
            payment_notes: notes || '[Confirmado via Alerta Rápido]'
          })
          .eq('id', entry.id);

        if (error) throw error;
      }

      toast({
        title: isDespesa(entry) ? '✅ Pagamento confirmado!' : '✅ Recebimento confirmado!',
        description: `${entry.description} — ${formatCurrency(entry.amount)}`,
      });

      setIsConfirmDialogOpen(false);
      setSelectedEntry(null);
      onEntryUpdate();
      
      // Atualizar categorias
      setTimeout(categorizeEntries, 300);
    } catch (error: any) {
      console.error('Erro ao confirmar operação financeira:', error);
      toast({
        title: "❌ Erro",
        description:
          error.message ||
          (isReceita(entry)
            ? 'Não foi possível confirmar o recebimento.'
            : 'Não foi possível confirmar o pagamento.'),
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmFromDialog = () => {
    if (selectedEntry) {
      confirmPayment(selectedEntry, paymentDate, paymentNotes);
    }
  };

  const totalEntries = 
    categorizedEntries.overdue.length + 
    categorizedEntries.today.length + 
    categorizedEntries.upcoming.length + 
    categorizedEntries.future.length;

  if (totalEntries === 0) {
    return (
      <div className="flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white p-5">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
          <CheckCircle className="h-5 w-5" />
        </span>
        <div>
          <p className="font-semibold text-slate-900">Tudo em dia</p>
          <p className="text-sm text-slate-500">Nenhuma conta a pagar ou a receber pendente nos próximos dias.</p>
        </div>
      </div>
    );
  }

  const pendingAll: CategorizedEntry[] = [
    ...categorizedEntries.overdue,
    ...categorizedEntries.today,
    ...categorizedEntries.upcoming,
    ...categorizedEntries.future,
  ];
  const hasDespesa = pendingAll.some(isDespesa);
  const hasReceita = pendingAll.some(isReceita);
  const sectionTitle =
    hasDespesa && hasReceita
      ? 'Central financeira'
      : hasReceita && !hasDespesa
        ? 'Central de recebimentos'
        : 'Central de pagamentos';

  const sum = (list: CategorizedEntry[]) => list.reduce((total, e) => total + e.amount, 0);
  const segments: { key: AlertCategory; label: string; list: CategorizedEntry[]; tone: string; dot: string }[] = [
    { key: 'overdue', label: 'Vencidos', list: categorizedEntries.overdue, tone: 'text-red-600', dot: 'bg-red-500' },
    { key: 'today', label: 'Vencem hoje', list: categorizedEntries.today, tone: 'text-amber-600', dot: 'bg-amber-500' },
    { key: 'upcoming', label: 'Próximos dias', list: categorizedEntries.upcoming, tone: 'text-slate-900', dot: 'bg-[#c4ab83]' },
    { key: 'future', label: 'Futuros', list: categorizedEntries.future, tone: 'text-slate-900', dot: 'bg-slate-300' },
  ];
  const activeEntries = categorizedEntries[activeTab] || [];

  const renderEntry = (entry: CategorizedEntry) => {
    const isExpense = isDespesa(entry);
    const accent =
      entry.category_type === 'overdue' ? 'bg-red-500' : entry.category_type === 'today' ? 'bg-amber-500' : 'bg-[#c4ab83]';

    return (
      <div
        key={entry.id}
        className="group flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[#faf7f2]/70"
      >
        <span className={cn('h-10 w-1 shrink-0 rounded-full', accent)} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="truncate text-sm font-medium text-slate-900">{entry.description}</span>
            {entry.is_recurring && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                <RefreshCw className="h-3 w-3" /> Recorrente
              </span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {format(parseISO(entry.payment_date!), 'dd/MM/yyyy')}
            </span>
            {entry.category_type === 'overdue' && (
              <span className="font-medium text-red-600">
                {entry.days_info} {entry.days_info === 1 ? 'dia' : 'dias'} em atraso
              </span>
            )}
            {(() => {
              const admin = (entry as any).administrative_process_id ? adminInfo[(entry as any).administrative_process_id] : undefined;
              const clientName = entry.clients?.name || (entry.case_id ? caseClientNames[entry.case_id] : undefined) || admin?.client;
              const caseTitle = entry.cases?.title || admin?.title;
              if (!clientName && !caseTitle) return null;
              return (
                <span className="inline-flex min-w-0 items-center gap-1">
                  <User className="h-3 w-3 shrink-0" />
                  {clientName && <span className="font-medium text-slate-700">{clientName}</span>}
                  {caseTitle && (
                    <span className="truncate text-slate-400" title={caseTitle}>
                      {clientName ? ' · ' : ''}{caseTitle}
                    </span>
                  )}
                </span>
              );
            })()}
          </div>
        </div>

        <span className={cn('shrink-0 text-sm font-semibold tabular-nums', isExpense ? 'text-red-600' : 'text-emerald-600')}>
          {isExpense ? '−' : '+'} {formatCurrency(entry.amount)}
        </span>

        <div className="flex shrink-0 items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleQuickConfirm(entry);
            }}
            disabled={isProcessing}
            className="h-8 rounded-lg border-emerald-200 px-3 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
          >
            <CheckCircle className="mr-1 h-3.5 w-3.5" />
            {isExpense ? 'Pagar' : 'Receber'}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              openConfirmDialog(entry);
            }}
            className="h-8 w-8 text-slate-400 hover:text-[#9a7f52]"
            title="Detalhes"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white animate-in fade-in slide-in-from-bottom-1 duration-300">
        {/* Cabeçalho */}
        <div className="relative overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] px-5 py-6 sm:px-6">
          <img
            src="/imgs/logos allana/letra_A_logo.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-3 top-1/2 h-32 w-32 -translate-y-1/2 select-none object-contain opacity-[0.12]"
          />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#c4ab83]/20 text-[#9a7f52]">
                <DollarSign className="h-6 w-6" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">{sectionTitle}</p>
                <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
                  {totalEntries} {totalEntries === 1 ? 'pendência' : 'pendências'}
                </p>
                <p className="mt-1 text-sm text-slate-500">Contas a receber e a pagar que precisam de confirmação.</p>
              </div>
            </div>
            {categorizedEntries.overdue.length > 0 ? (
              <div className="sm:text-right">
                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 sm:justify-end">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                  Vencidos
                </p>
                <p className="text-2xl font-semibold tabular-nums tracking-tight text-red-600">
                  {formatCurrency(sum(categorizedEntries.overdue))}
                </p>
              </div>
            ) : (
              <div className="sm:text-right">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Total pendente</p>
                <p className="text-2xl font-semibold tabular-nums tracking-tight text-slate-900">{formatCurrency(sum(pendingAll))}</p>
              </div>
            )}
          </div>
        </div>

        {/* Segmentos (funcionam como abas) */}
        <div className="grid grid-cols-2 gap-px border-b border-slate-100 bg-slate-100 sm:grid-cols-4">
          {segments.map((segment) => {
            const active = activeTab === segment.key;
            const empty = segment.list.length === 0;
            return (
              <button
                key={segment.key}
                type="button"
                onClick={() => setActiveTab(segment.key)}
                className={cn(
                  'relative px-5 py-3 text-left transition-colors',
                  active ? 'bg-[#faf7f2]' : 'bg-white hover:bg-[#faf7f2]/60'
                )}
              >
                {active && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[#c4ab83]" />}
                <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                  <span className={cn('h-1.5 w-1.5 rounded-full', segment.dot)} />
                  {segment.label} · {segment.list.length}
                </span>
                <span className={cn('mt-1 block text-lg font-semibold tabular-nums tracking-tight', empty ? 'text-slate-300' : segment.tone)}>
                  {formatCurrency(sum(segment.list))}
                </span>
              </button>
            );
          })}
        </div>

        {/* Lançamentos */}
        <div className="max-h-[420px] overflow-y-auto">
          <div className="divide-y divide-slate-100">
            {activeEntries.length > 0 ? (
              activeEntries.map(renderEntry)
            ) : (
              <p className="px-5 py-8 text-center text-sm text-slate-400">Nada nesta categoria.</p>
            )}
          </div>
        </div>
      </div>

      {/* Dialog de confirmação detalhada */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="w-[95vw] max-w-xl overflow-hidden border border-[#c4ab83]/30 bg-white shadow-2xl">
          {selectedEntry && (() => {
            const expense = isDespesa(selectedEntry);
            const admin = (selectedEntry as any).administrative_process_id ? adminInfo[(selectedEntry as any).administrative_process_id] : undefined;
            const clientName =
              selectedEntry.clients?.name ||
              (selectedEntry.case_id ? caseClientNames[selectedEntry.case_id] : undefined) ||
              admin?.client;
            const processTitle = selectedEntry.cases?.title || admin?.title;
            return (
              <>
                <DialogHeader className="relative -m-6 mb-5 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
                  <img
                    src="/imgs/logos allana/letra_A_logo.png"
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-4 top-1/2 h-32 w-32 -translate-y-1/2 select-none object-contain opacity-[0.12]"
                  />
                  <div className="relative text-left">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">
                      {expense ? 'Confirmar pagamento' : 'Confirmar recebimento'}
                    </p>
                    <DialogTitle className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                      {selectedEntry.description}
                    </DialogTitle>
                    <p className={cn('mt-1 text-3xl font-semibold tabular-nums tracking-tight', expense ? 'text-red-600' : 'text-emerald-600')}>
                      {expense ? '−' : '+'} {formatCurrency(selectedEntry.amount)}
                    </p>
                  </div>
                </DialogHeader>

                <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                      {expense ? 'Vencimento' : 'Previsão'}
                    </dt>
                    <dd className="mt-0.5 text-sm text-slate-800">{format(parseISO(selectedEntry.payment_date!), 'dd/MM/yyyy')}</dd>
                  </div>
                  {clientName && (
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Cliente</dt>
                      <dd className="mt-0.5 text-sm font-medium text-slate-800">{clientName}</dd>
                    </div>
                  )}
                  {processTitle && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Processo</dt>
                      <dd className="mt-0.5 truncate text-sm text-slate-800" title={processTitle}>{processTitle}</dd>
                    </div>
                  )}
                  {selectedEntry.is_recurring && (
                    <div className="sm:col-span-2">
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                        <RefreshCw className="h-3 w-3" /> Lançamento recorrente
                      </span>
                    </div>
                  )}
                </dl>

                <div className="mt-5 space-y-4 border-t border-slate-100 pt-5">
                  <div>
                    <Label htmlFor="payment_date" className="mb-1.5 block text-sm font-medium text-slate-700">
                      {expense ? 'Data do pagamento' : 'Data do recebimento'}
                    </Label>
                    <Input
                      id="payment_date"
                      type="date"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      className="rounded-lg border-slate-200 shadow-none focus-visible:ring-[#c4ab83]/30 sm:w-56"
                    />
                  </div>
                  <div>
                    <Label htmlFor="payment_notes" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Observações <span className="font-normal text-slate-400">(opcional)</span>
                    </Label>
                    <Textarea
                      id="payment_notes"
                      value={paymentNotes}
                      onChange={(e) => setPaymentNotes(e.target.value)}
                      placeholder={expense ? 'Ex.: pago via PIX, comprovante anexado…' : 'Ex.: recebido via PIX, depósito identificado…'}
                      className="rounded-lg border-slate-200 shadow-none focus-visible:ring-[#c4ab83]/30"
                      rows={2}
                    />
                  </div>
                </div>

                <DialogFooter className="mt-2 gap-2 border-t border-slate-100 pt-4">
                  <Button variant="outline" className="rounded-xl" onClick={() => setIsConfirmDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleConfirmFromDialog}
                    disabled={isProcessing}
                    className="rounded-xl bg-slate-900 text-white hover:bg-slate-800"
                  >
                    {isProcessing ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Confirmando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4 text-emerald-300" />
                        {expense ? 'Confirmar pagamento' : 'Confirmar recebimento'}
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SmartPaymentAlerts;
